import { useCallback, useLayoutEffect } from "react";
import { FocusMode } from "./useFocusMode";

export interface UseFocusEventListenerOptions {
    focusMode: FocusMode;
    keyCodes: Set<KeyboardEvent["key"]>;
    keyEvents: Set<keyof HTMLElementEventMap>;
    mouseEvents: Set<keyof HTMLElementEventMap>;
    setFocusMode: (newFocusMode: FocusMode) => void;
}

/**
 * Listens to certain events to turn focus on or off. It will performantly add/remove event
 * handlers so we aren't always listening to mouse AND keyboard events, just one or the other.
 * This goes hand in hand with useFocusMode.
 */
export const useFocusEventListener = ({
    focusMode,
    keyCodes,
    keyEvents,
    mouseEvents,
    setFocusMode,
}: UseFocusEventListenerOptions): void => {
    const setKeyboard = useCallback(
        (event: Event) => {
            // Only pay attention to certain keys.
            if (keyCodes.has((event as KeyboardEvent).key)) {
                setFocusMode("keyboard");
            }
        },
        [keyCodes, setFocusMode]
    );
    const setMouse = useCallback(() => setFocusMode("mouse"), [setFocusMode]);

    const watchForKeyboardOn = useCallback(() => {
        keyEvents.forEach((eventName) => {
            document.body.addEventListener(eventName, setKeyboard);
        });
    }, [setKeyboard, keyEvents]);

    const watchForKeyboardOff = useCallback(() => {
        keyEvents.forEach((eventName) => {
            document.body.removeEventListener(eventName, setKeyboard);
        });
    }, [setKeyboard, keyEvents]);

    const watchForMouseOn = useCallback(() => {
        mouseEvents.forEach((eventName) => {
            document.body.addEventListener(eventName, setMouse);
        });
    }, [setMouse, mouseEvents]);

    const watchForMouseOff = useCallback(() => {
        mouseEvents.forEach((eventName) => {
            document.body.removeEventListener(eventName, setMouse);
        });
    }, [setMouse, mouseEvents]);

    useLayoutEffect(() => {
        switch (focusMode) {
            case "mouse":
                watchForKeyboardOn();
                break;
            case "keyboard":
                watchForMouseOn();
                break;
        }

        return () => {
            watchForKeyboardOff();
            watchForMouseOff();
        };
    }, [focusMode, watchForKeyboardOff, watchForKeyboardOn, watchForMouseOff, watchForMouseOn]);
};
